import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { productsApi } from "@/lib/catalog/catalog-api";
import { customersApi } from "@/lib/customers/customers-api";
import { ordersApi } from "@/lib/orders/orders-api";

export interface GlobalHit {
	kind: "order" | "product" | "customer";
	id: string;
	/** Ce qu'on lit en premier : numéro de commande, titre produit, nom client. */
	label: string;
	/** Ligne secondaire : montant, référence, téléphone. */
	hint?: string;
	path: string;
}

const RUBRIQUES: Record<GlobalHit["kind"], string> = {
	order: "Commandes",
	product: "Produits",
	customer: "Clients",
};

export function libelleRubrique(kind: GlobalHit["kind"]): string {
	return RUBRIQUES[kind];
}

/**
 * Recherche globale du back-office.
 *
 * La barre annonçait « commande, client, produit, ticket » mais se contentait
 * de rediriger vers la recherche de commandes, sans rien proposer pendant la
 * frappe : chercher un produit par son nom depuis l'accueil ne donnait jamais
 * rien, et personne ne pouvait deviner pourquoi.
 *
 * Trois sources interrogées en parallèle, avec une temporisation : on ne lance
 * rien tant que la frappe continue, ce qui compte sur une connexion mobile
 * camerounaise.
 */
export function useGlobalSearch(terme: string) {
	const [differe, setDiffere] = useState(terme);

	useEffect(() => {
		const t = setTimeout(() => setDiffere(terme.trim()), 250);
		return () => clearTimeout(t);
	}, [terme]);

	// Deux caractères ne discriminent rien et feraient remonter la moitié du
	// catalogue à chaque frappe.
	const actif = differe.length >= 3;

	const query = useQuery({
		queryKey: ["global-search", differe],
		enabled: actif,
		staleTime: 30_000,
		retry: false,
		queryFn: async (): Promise<GlobalHit[]> => {
			// `allSettled` : une source indisponible (droits insuffisants sur le
			// catalogue, par exemple) ne doit pas vider tout le panneau.
			// Un numéro de commande se cherche par son numéro ; tout le reste se
			// cherche sur le client. Les deux champs sont distincts côté serveur.
			const ressembleAUnNumero = /^[A-Za-z]{2,4}-?\d/.test(differe);
			const filtreCommandes = ressembleAUnNumero
				? { number: differe, take: 4 }
				: { customerQuery: differe, take: 4 };

			const [commandes, produits, clients] = await Promise.allSettled([
				ordersApi.list(filtreCommandes),
				productsApi.list({ search: differe, pageSize: 4 }),
				customersApi.list({ q: differe, limit: 4 }),
			]);

			const hits: GlobalHit[] = [];

			if (commandes.status === "fulfilled") {
				for (const o of commandes.value.items ?? []) {
					hits.push({
						kind: "order",
						id: o.id,
						label: o.number,
						hint: o.customer
							? `${o.customer.firstName ?? ""} ${o.customer.lastName ?? ""}`.trim() ||
								(o.customer.phone ?? undefined)
							: undefined,
						path: `/commandes/${o.id}`,
					});
				}
			}

			if (produits.status === "fulfilled") {
				for (const p of produits.value.items ?? []) {
					hits.push({
						kind: "product",
						id: p.id,
						label: p.title,
						hint: p.sku,
						path: `/catalogue/${p.id}`,
					});
				}
			}

			if (clients.status === "fulfilled") {
				for (const c of clients.value.items ?? []) {
					hits.push({
						kind: "customer",
						id: c.id,
						label: c.name?.trim() || c.phone || "Client",
						hint: c.phone ?? undefined,
						path: `/clients/${c.id}`,
					});
				}
			}

			return hits;
		},
	});

	return {
		hits: query.data ?? [],
		isLoading: actif && query.isLoading,
		actif,
	};
}
